"use client";

import type React from "react";
import { useState, useRef } from "react";
import { HStack } from "@chakra-ui/react";
import { useAppTheme } from "../ThemeContext.tsx";

interface OTPInputProps {
  onComplete: (otp: string) => void;
}

const OTPInput: React.FC<OTPInputProps> = ({ onComplete }) => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define colors based on theme
  const bgColor = isDark ? "#2D3748" : "#EDF2F7"; // dark: gray.800, light: gray.200/gray.100 equivalent
  const textColor = isDark ? "white" : "black";
  const borderColor = isDark ? "#4A5568" : "#E2E8F0";

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every((digit) => digit !== "")) {
      onComplete(newOtp.join(""));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (pastedData.length === 6) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      onComplete(pastedData);
      inputRefs.current[5]?.focus();

      // Manually update each input field
      newOtp.forEach((digit, index) => {
        if (inputRefs.current[index]) {
          const input = inputRefs.current[index] as HTMLInputElement;
          input.value = digit;
        }
      });
    }
  };

  // Define common style for the input fields
  const inputStyle: React.CSSProperties = {
    width: "40px",
    height: "40px",
    textAlign: "center",
    fontSize: "1.2rem",
    backgroundColor: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    borderRadius: "0.375rem",
    outline: "none",
  };

  return (
    <HStack spacing={4}>
      {Array.from({ length: 6 }).map((_, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          value={otp[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onPaste={index === 0 ? handlePaste : undefined}
          ref={(el) => (inputRefs.current[index] = el)}
          style={inputStyle}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </HStack>
  );
};

export default OTPInput;
